import React, { useState, useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  Chip,
  Button,
  Avatar,
  Alert
} from '@mui/material';
import {
  Payment as MoneyIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { PaymentUtils } from '../utils/paymentService';
import RenderRazorpay from '../pages/layout/agents/RenderRazorpay';
import { toast } from 'react-toastify';
import { Post, apiURL } from '../utils/apiCalls';
const PaymentRequestCard = ({ paymentRequest }) => {
  const [displayRazorpay, setDisplayRazorpay] = useState(false);
  const [orderDetails, setOrderDetails] = useState({
    orderId: null,
    currency: null,
    amount: null,
  });
  const [account, setAccount] = useState();

  const userDetails = sessionStorage.getItem('user_details');
  useMemo(() => {
    setAccount(JSON.parse(userDetails));
  }, [userDetails]);

  const handlePaymentClick = async () => {
    try {
      // Create order for layout payment
      const orderData = {
        pay_amount: paymentRequest.amount , // Convert to paise
        currency: paymentRequest.currency || 'INR',
        receipt: `layout_payment_${paymentRequest.id}`,
        payment_request_id: paymentRequest.id,
        layout_payment_request_id:paymentRequest.id,
        layout_request_id:paymentRequest.layout_request_id
 
      };

      // For now, create a mock order ID (in real implementation, call your API)


      const response = await Post(apiURL + '/api/v1/payments/procurement/payment_order',orderData)
      if (response && response.data && response.data.status) {
         console.log(response.data,"response.data")
         setOrderDetails({
            orderId: response.data.data.order_id,
            currency: response.data.data.currency,
            amount: response.data.data.amount,
          });
          setDisplayRazorpay(true);
      }
       else {
          toast.error(response?.data.message || 'Please Try Again')
       }
    } catch (error) {
      console.error('Error creating payment order:', error);
      toast.error('Failed to initiate payment');
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return <ScheduleIcon />;
      case 'processing':
        return <PaymentIcon />;
      case 'completed':
      case 'paid':
        return <CheckCircleIcon />;
      case 'failed':
      case 'cancelled':
        return <ErrorIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const getStatusColor = (status) => {
    return PaymentUtils.getStatusColor(status);
  };

  const getStatusText = (status) => {
    return PaymentUtils.getStatusText(status);
  };

  const isOverdue = PaymentUtils.isOverdue(paymentRequest.created_at);
  const isFinalStatus = PaymentUtils.isFinalStatus(paymentRequest.status);
  const canPay = paymentRequest.status === 'pending' && !isOverdue;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDueDate = (dateString) => {
    const dueDate = PaymentUtils.calculateDueDate(dateString);
    return dueDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <>
      <Card 
        sx={{ 
          mb: 2,
          borderRadius: 3,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          transition: 'all 0.2s ease-in-out',
          border: isOverdue ? '2px solid #f44336' : '1px solid #e0e0e0',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 20px rgba(0,0,0,0.15)'
          }
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" alignItems="flex-start" spacing={2}>
            <Avatar 
              sx={{ 
                bgcolor: getStatusColor(paymentRequest.status),
                width: 48,
                height: 48
              }}
            >
              {getStatusIcon(paymentRequest.status)}
            </Avatar>
            
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="h6" fontWeight="bold" color="text.primary">
                  {paymentRequest.title || 'Layout Request Payment'}
                </Typography>
                <Chip
                  label={getStatusText(paymentRequest.status)}
                  size="small"
                  sx={{
                    bgcolor: getStatusColor(paymentRequest.status),
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
              </Stack>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {paymentRequest.description || 'Payment for approved layout request'}
              </Typography>

              <Stack direction="row" alignItems="center" spacing={3} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MoneyIcon color="primary" />
                  <Typography variant="h5" fontWeight="bold" color="primary">
                    ₹{paymentRequest.amount?.toLocaleString()}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ReceiptIcon color="action" />
                  <Typography variant="body2" color="text.secondary">
                    Due: {formatDueDate(paymentRequest.created_at)}
                  </Typography>
                </Box>
              </Stack>

              {isOverdue && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Payment is overdue! Please make payment immediately.
                </Alert>
              )}

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  Request ID: {paymentRequest.id} • Created: {formatDate(paymentRequest.created_at)}
                </Typography>
                
                {canPay && (
                  <Button
                    variant="contained"
                    startIcon={<PaymentIcon />}
                    onClick={handlePaymentClick}
                    sx={{
                      bgcolor: '#4caf50',
                      '&:hover': { bgcolor: '#45a049' }
                    }}
                  >
                    Pay Now
                  </Button>
                )}
                
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Render Razorpay Component - Same pattern as existing implementations */}
      {displayRazorpay && (
         <RenderRazorpay
         amount={orderDetails.amount}
         currency={orderDetails.currency}
         orderId={orderDetails.orderId}
         keyId={'rzp_live_T0Fj5jdM5C2Q6F'}
         page={'layout_payment'}
                    keySecret={''}
                    email={account.email}
                    phone={account.phone}
       />
      )}
    </>
  );
};

export default PaymentRequestCard;
